import { PUBLIC_SITE_URL } from '@/lib/urls';
import { toast } from 'sonner';
import type { MemberRole } from '@/types/database';

/**
 * Team-invite helpers (Phase 3).
 *
 * Invites are bearer links: the token embedded in the URL is the secret. The
 * link is created client-side (RLS lets owner/admin insert into
 * `company_invites`) and shared over WhatsApp / the native share sheet. The
 * server-side RPCs `preview_company_invite` / `accept_company_invite` validate
 * and consume it. See migration 20260720120000.
 */

/** 256 bits of url-safe entropy (two UUIDs, hex, no dashes). */
export function generateInviteToken(): string {
  return `${crypto.randomUUID()}${crypto.randomUUID()}`.replace(/-/g, '');
}

/** Public accept URL for an invite token. */
export function getInviteLink(token: string): string {
  return `${PUBLIC_SITE_URL}/convite/${token}`;
}

/** Human-readable, pt-BR role names. */
export const ROLE_LABELS: Record<MemberRole, string> = {
  owner: 'Dono',
  admin: 'Administrador',
  member: 'Membro',
  viewer: 'Visualizador',
};

/** Short description of what each role can do, for the invite UI. */
export const ROLE_DESCRIPTIONS: Record<MemberRole, string> = {
  owner: 'Controle total da empresa e da equipe.',
  admin: 'Gerencia orçamentos, clientes, catálogo, financeiro e equipe.',
  member: 'Cria e edita orçamentos, clientes e catálogo. Sem financeiro.',
  viewer: 'Apenas visualiza. Não cria nem edita nada.',
};

/** Roles that can be assigned through the team UI (never 'owner'). */
export const ASSIGNABLE_ROLES: MemberRole[] = ['admin', 'member', 'viewer'];

async function writeToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();

  if (!copied) throw new Error('Clipboard unavailable');
}

function inviteMessage(companyName: string, url: string): string {
  return `Você foi convidado para participar da equipe de "${companyName}" no Orçamigo. Toque no link para aceitar: ${url}`;
}

export async function copyInviteLink(token: string): Promise<void> {
  await writeToClipboard(getInviteLink(token));
  toast.success('Link do convite copiado!');
}

/** Share via the native sheet (falls back to clipboard). */
export async function shareInviteLink(token: string, companyName: string): Promise<void> {
  const url = getInviteLink(token);

  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Convite para equipe — Orçamigo',
        text: inviteMessage(companyName, url),
        url,
      });
      return;
    } catch (error) {
      if ((error as DOMException)?.name === 'AbortError') return;
    }
  }

  await writeToClipboard(url);
  toast.success('Link do convite copiado!');
}

/** Open WhatsApp with the invite message prefilled. */
export function shareInviteViaWhatsApp(token: string, companyName: string): void {
  const url = getInviteLink(token);
  const text = encodeURIComponent(inviteMessage(companyName, url));
  window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
}
